import Splide from '@splidejs/splide';
import {sliderOptions} from '../colorz/configClrzApp';
import {syncSplideSlidesInert} from '../colorz/utils/ally/syncSplideSlidesInert';

export default class ClrzSliderNoDefer extends HTMLElement {
	constructor() {
		super();
	}

	connectedCallback() {
		this.defaultOptions = sliderOptions.default;
		this.options = !!this.dataset.options
			? sliderOptions[this.dataset.options]
			: {};
		this.options = {...this.defaultOptions, ...this.options};

		// specific dev for this slider, how may have arrows
		if (this.dataset.options == 'js-slider-cart-drawer-side') {
			if (this.dataset.slides > 1) {
				this.options.arrows = true;
			}
		}

		if (this.dataset.options === 'js-slider-push-edito-full') {
			const slidesCount = Number(this.dataset.slides) || 0;
			if (slidesCount <= 1) {
				this.options.arrows = false;
				this.options.pagination = false;
				this.options.drag = false;
			}
		}

		if (this.dataset.destroy == 'true') {
			this.options.destroy = true;
		}

		this.initSlider();
	}

	initSlider() {
		if (!this.options.direction) {
			this.options.direction =
				document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';
		}

		const splide = new Splide(this, this.options);
		this.splide = splide;

		splide.on('overflow', function (isOverflow) {
			if (!isOverflow) {
				splide.options = {
					arrows: false,
					pagination: false,
					drag: false,
				};
			}
		});

		if (this.dataset.options === 'js-slider-push-edito-full') {
			const ctaColorProperties = [
				'--push-edito-cta-background',
				'--push-edito-cta-color',
				'--push-edito-cta-hover-background',
				'--push-edito-cta-hover-color',
			];
			const layout = this.closest('[data-push-edito-full-layout]');
			const media = layout?.querySelector('[data-push-edito-full-media]');
			const mediaItems = layout
				? layout.querySelectorAll('[data-push-edito-media]')
				: [];
			const applyMediaAspectRatio = (img) => {
				if (!media || !img) return;

				const width = img.naturalWidth;
				const height = img.naturalHeight;
				const isPortrait = width > 0 && height > width;

				// Landscape / default: 5/4 — Portrait: 4/5
				const aspectRatio = isPortrait ? '4 / 5' : '5 / 4';

				// refresh() re-emits `active`, which calls this again: only refresh
				// when the ratio actually changes to avoid an infinite loop.
				if (media.style.aspectRatio === aspectRatio) return;

				media.style.aspectRatio = aspectRatio;

				requestAnimationFrame(() => {
					splide.refresh();
				});
			};

			const syncMediaAspectRatio = (activeItem) => {
				const img = activeItem?.querySelector('img');
				if (!img) return;

				const onReady = () => applyMediaAspectRatio(img);

				if (img.complete && img.naturalWidth) {
					onReady();
					return;
				}

				img.addEventListener('load', onReady, {once: true});
				img.addEventListener('lazyloaded', onReady, {once: true});
			};

			const syncPushEditoFull = (slideComponent) => {
				const activeSlide =
					slideComponent?.slide ||
					this.querySelector('.splide__slide.is-active');

				if (!activeSlide) return;

				const activeSlideStyles = getComputedStyle(activeSlide);
				ctaColorProperties.forEach((property) => {
					this.style.setProperty(
						property,
						activeSlideStyles.getPropertyValue(property),
					);
				});

				const activeIndex = Number(activeSlide.dataset.pushEditoSlide);
				let activeMediaItem = null;

				mediaItems.forEach((item) => {
					const isActive = Number(item.dataset.pushEditoMedia) === activeIndex;
					item.classList.toggle('is-active', isActive);
					item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
					if (isActive) activeMediaItem = item;
				});

				syncMediaAspectRatio(activeMediaItem);
			};

			splide.on('mounted active', syncPushEditoFull);
		}

		splide.mount();
		syncSplideSlidesInert(this);

		if (this.dataset.options === 'js-slider-push-edito-full') {
			let sliderWidth = this.clientWidth;

			requestAnimationFrame(() => {
				splide.refresh();
				sliderWidth = this.clientWidth;
			});

			if ('ResizeObserver' in window) {
				this.resizeObserver = new ResizeObserver(() => {
					if (this.clientWidth === sliderWidth) return;

					sliderWidth = this.clientWidth;
					splide.refresh();
				});
				this.resizeObserver.observe(this);
			}
		}

		const prevArrow = this.querySelector('.splide__arrow--prev');
		const nextArrow = this.querySelector('.splide__arrow--next');
		if (prevArrow && nextArrow && this.options.arrows) {
			let activatedArrow = null;
			const markPrev = () => {
				activatedArrow = 'prev';
			};
			const markNext = () => {
				activatedArrow = 'next';
			};
			prevArrow.addEventListener('pointerdown', markPrev);
			nextArrow.addEventListener('pointerdown', markNext);
			prevArrow.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') markPrev();
			});
			nextArrow.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') markNext();
			});

			splide.on('moved', () => {
				const which = activatedArrow;
				activatedArrow = null;
				queueMicrotask(() => {
					const focused = document.activeElement;
					if (
						this.dataset.options === 'js-slider-cart-drawer-side' &&
						splide.index === 0 &&
						prevArrow.disabled &&
						!nextArrow.disabled &&
						(focused === prevArrow || focused === nextArrow)
					) {
						nextArrow.focus({preventScroll: true});
						return;
					}
					if (!which) return;
					if (which === 'prev' && prevArrow.disabled && !nextArrow.disabled) {
						nextArrow.focus({preventScroll: true});
					} else if (
						which === 'next' &&
						nextArrow.disabled &&
						!prevArrow.disabled
					) {
						prevArrow.focus({preventScroll: true});
					}
				});
			});
		}

		if (this.dataset.positionArrows == 'true') {
			const topArrow =
				this.querySelector('.splide__slide img')?.offsetHeight / 2;
			this.querySelectorAll('.splide__arrow-wrapper').forEach((arrow) => {
				arrow.style.top = topArrow + 'px';
			});
		}

		const slides = this.querySelectorAll('.splide__slide');

		slides.forEach(function (slide) {
			const noDragElements = slide.querySelector('.js-stop-drag-slider');
			const dragElements = slide.querySelectorAll('.js-drag-slider');

			if (noDragElements) {
				noDragElements.addEventListener('click', function () {
					splide.options = {drag: false};
					if (this.closest('.splide__list.remove-transform')) {
						this.closest('.splide__list.remove-transform').style.transform = '';
					}
				});

				dragElements.forEach(function (element) {
					element.addEventListener('click', function () {
						splide.options = {drag: true};
					});
				});
			}
		});
	}

	disconnectedCallback() {
		this.resizeObserver?.disconnect();
		this.splide?.destroy();
	}
}
